import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import useLanguageChange from "../../../../hooks/useLanguageChange";
import mediaDevice from "../../../../services/mediaDeviceService";
import { ERROR_TYPE, MEDIA_PERMISSIONS } from "../../../../constants/CallSettingConstants";
import soundMp3 from "../../../../assets/audio/user_join.mp3";
import navigatorService from "../../../../services/navigatorService";
import VideoQualityDropDown from "./VideoQualityDropDown";
import { GLOBAL_MEETING_DATA, PublicVariables } from "../../../../constants/PublicVariables";
import SpeakerDropDown from "./SpeakerDropDown";
import MicrophoneDropDown from "./MicrophoneDropDown";
import CameraDropDown from "./CameraDropDown";
import { changeVideoInput, setAllMediaDevices, showCallSettingLoadingView, showCallSettingView } from "./CallSettingUtils";
import useCheckCallConnected from "../../../../hooks/useCheckCallConnected";
import { activateAudioTrackMicTesting, applyAudioInputDeviceSelection } from "../../../Twilio/twilioCallUtility";
import {
    AGENT_PRE_CALL_NETWORK_TEST_LINK,
    AR_MARKUP_VIEW,
    CALL_BODY_SCREEN,
    ENABLED,
    EXTRACT_TEXT_VIEW,
    KEYBOARD_KEYS,
    MARKUP_VIEW,
    MEASUREMENT_VIEW,
} from "../../../../constants/Constants";
import { showLocalVideoInCallSettings } from "../../../Twilio/VideoCall/VideoUtils";
import { setVideoError } from "../../../../reducers/twilioEvents-reducer";
import VirtualBackground from "../../../VirtualBackground/VirtualBackground";
import { isMobileSafariBelow18, isVirtualBackGroundFeatureAvailable } from "../../../../shared/utility";
import { setIsRemoveVirtualBackgroundUploadImage } from "../../../../reducers/call-setting-reducer";
import CustomToast from "../../../UI/customToast";
import { LogFirebase } from "../../../../services/firestore";

const CallSettingDrawer = () => {
    const dispatch = useDispatch();
    const isCallNotConnected = useCheckCallConnected();
    const { cameraPermission, audioPermission, isRemoveVirtualBackgroundUploadImage } = useSelector((state) => state.callSetting);
    const { startLocalAudio, startLocalVideo, videoError } = useSelector((state) => state.twilioEvents);
    const { isCallSettingDrawerOpen, expandView, currentCallBodyScreen, isRoomConnected, isCallDisconnected } = useSelector(
        (state) => state.commonReducer
    );
    const { markupInprogress, markupLiveStream, arMarkupLiveStream } = useSelector((state) => state.markup);
    const { extractTextInprogress } = useSelector((state) => state.extractText);
    const { measurementView } = useSelector((state) => state.measurement);

    const localisedValues = useLanguageChange();

    /**when user clicks on grant permission  */
    const askPermission = async (isFromCallsetting) => {
        if (audioPermission != MEDIA_PERMISSIONS.GRANTED || cameraPermission != MEDIA_PERMISSIONS.GRANTED || isFromCallsetting == "call-setting") {
            //any one device has permission
            let fromWhere = null;
            if (startLocalVideo && !startLocalAudio) {
                fromWhere = "audioOnly";
            }
            if (startLocalAudio && !startLocalVideo) {
                fromWhere = "videoOnly";
            }
            const res = await mediaDevice.getMediaDevices();
            if (res) {
                if (videoError == ERROR_TYPE.NOT_READABLE) {
                    dispatch(setVideoError(null));
                    //select first camera in drop down
                    mediaDevice.selectReadableErrorCam();
                }
                mediaDevice.getUserMedia(fromWhere).then(function () {
                    mediaDevice.hideCallSettingCamLoading();
                    showCallSettingView();
                    //updated device if any has permission as prompt
                    if (startLocalAudio) {
                        activateAudioTrackMicTesting();
                    }
                    if (startLocalVideo) {
                        showLocalVideoInCallSettings();
                    }
                    setAllMediaDevices();
                });
            }
        }
    };

    // it will play sound
    const handlePlaySound = () => {
        const audio = new Audio(soundMp3);
        audio.play();
    };

    const audioVideoStarthandler = async () => {
        const res = await mediaDevice.getMediaDevices();
        if (res) {
            mediaDevice.getAudioSource();
            activateAudioTrackMicTesting();
            showLocalVideoInCallSettings();
            setAllMediaDevices(); // prepareCallSettings
            showCallSettingView();
            mediaDevice.hideCallSettingCamLoading();
        }
    };

    useEffect(() => {
        // showCallSettings functon
        if (isCallSettingDrawerOpen) {
            showCallSettingLoadingView();
            if (startLocalVideo && startLocalAudio) {
                // if audio and video is on
                audioVideoStarthandler();
            } else if (audioPermission === MEDIA_PERMISSIONS.GRANTED || cameraPermission === MEDIA_PERMISSIONS.GRANTED) {
                askPermission("call-setting");
            } else {
                mediaDevice.hideCallSettingCamLoading(); // hide camera loading
                showCallSettingView();
            }
        }
    }, [isCallSettingDrawerOpen]);

    const audioDeviceUpdateHandler = () => {
        if (audioPermission === MEDIA_PERMISSIONS.GRANTED) {
            if (startLocalAudio) {
                // changeAudioVideoInput old function
                applyAudioInputDeviceSelection();
            } else {
                let constraints = null;
                if (!isCallNotConnected && startLocalVideo) {
                    constraints = "audioOnly";
                }
                mediaDevice.getUserMedia(constraints);
            }
        }
    };

    const cameraDeviceUpdateHandler = () => {
        if (cameraPermission === MEDIA_PERMISSIONS.GRANTED) {
            if (startLocalVideo) {
                if (document.getElementById("showCamLoading")) {
                    document.getElementById("showCamLoading").style.display = "flex";
                }
                if (document.getElementById("showCamLoading_vb")) {
                    document.getElementById("showCamLoading_vb").style.display = "flex";
                }
                // changeAudioVideoInput function for camera
                changeVideoInput();
            } else {
                let constraints = null;
                if (!isCallNotConnected && startLocalAudio) {
                    constraints = "videoOnly";
                }
                mediaDevice.getUserMedia(constraints);
            }
        }
    };

    const cameraErrorMsg = useMemo(() => {
        let text = "";
        if (cameraPermission === MEDIA_PERMISSIONS.BLOCKED) {
            if (!navigatorService.isSafari()) {
                text = (
                    <div className='call-settings-video-text'>
                        {`${localisedValues["allow_access_to_camera"]} ${localisedValues["click_the_padlock"]} `}
                        <span className='for-inline-icon icon-lock'></span> {localisedValues["to_the_left_of_a_web_page_address"]}
                    </div>
                );
            } else {
                text = (
                    <div className='call-settings-video-text'>
                        {localisedValues["camera_permission_disabled"]} {localisedValues["permission_error_message_camera_browser"]}
                    </div>
                );
            }
        } else if (cameraPermission === MEDIA_PERMISSIONS.ERROR) {
            text = <div className='call-settings-video-text'>{localisedValues["camera_permission_disabled"]}</div>;
        }

        return text;
    }, [cameraPermission, localisedValues]);

    const openCallTestLink = () => {
        /*  It is not just for the agent; we will keep the condition as it is for old code. 
            However, in the call drawer, we show the network test page for all participants.
        */
        window.open(AGENT_PRE_CALL_NETWORK_TEST_LINK, "_blank");
    };

    const handleCallSettingModalOnEscKey = (e) => {
        if (e.key === "Escape" && e.keyCode == KEYBOARD_KEYS.ESCAPE) {
            if (isRemoveVirtualBackgroundUploadImage) {
                dispatch(setIsRemoveVirtualBackgroundUploadImage(false)); // close virtual background upload image modal
            } else {
                document.getElementById("closeTheCallSettingModal")?.click(); // close the call setting modal
            }
        }
    };

    useEffect(() => {
        if (isCallDisconnected) {
            document.getElementById("closeTheCallSettingModal")?.click(); // close the call setting modal
            dispatch(setIsRemoveVirtualBackgroundUploadImage(false)); // close the remove virtual background image modal
        }
    }, [isCallDisconnected]);

    useEffect(() => {
        const uploadFilesView = [CALL_BODY_SCREEN.NOTIFY_SCREEN, CALL_BODY_SCREEN.UPLOAD_FILE_SCREEN].includes(currentCallBodyScreen);
        const othersScreenView =
            markupInprogress !== MARKUP_VIEW.OFF_VIEW ||
            extractTextInprogress !== EXTRACT_TEXT_VIEW.OFF_VIEW ||
            measurementView !== MEASUREMENT_VIEW.OFF_VIEW ||
            arMarkupLiveStream !== AR_MARKUP_VIEW.OFF_VIEW ||
            markupLiveStream;
        if ((PublicVariables.featureFlag.SHOWMEDUALSTREAM == ENABLED && expandView && isRoomConnected) || uploadFilesView || othersScreenView) {
            document.getElementById("closeTheCallSettingModal")?.click(); // close the call setting modal
            dispatch(setIsRemoveVirtualBackgroundUploadImage(false)); // close the remove virtual background image modal
        }
    }, [expandView, currentCallBodyScreen, isRoomConnected, markupInprogress, extractTextInprogress, measurementView, markupLiveStream]);

    const handleVBNotSupported = () => {
        CustomToast.info(localisedValues?.virtual_background_not_supported);
        LogFirebase("VirtualBackground", "click_VB_not_supported", "click");
    };

    return (
        <div
            className='modal fade right-modal'
            data-bs-keyboard='false'
            onKeyDown={handleCallSettingModalOnEscKey}
            id='call-settings-sidebar'
            tabIndex='-1'
            aria-hidden='true'
        >
            <div className='modal-dialog modal-dialog-centered'>
                <div className='modal-content'>
                    <div className='modal-header'>
                        <h5 className='modal-title'>{localisedValues["settings"]}</h5>
                        <button id='closeTheCallSettingModal' type='button' className='btn-close' data-bs-dismiss='modal' aria-label='Close'>
                            <span className='btn-icon icon-close-light'></span>
                        </button>
                    </div>
                    <div className={`modal-body call-settings-modal-body ${navigatorService.isMobileDevice() && "with-modal-footer"}`}>
                        <div className='call-settings-box' id='call_setting_loader_end'>
                            <div className='nav-tab nav-tab-border call-settings-tab-nav'>
                                <ul className='nav nav-tabs' role='tablist'>
                                    <li className='nav-item' role='presentation'>
                                        <button
                                            className='nav-link active'
                                            data-bs-toggle='tab'
                                            data-bs-target='#call-settings'
                                            type='button'
                                            role='tab'
                                            aria-selected='true'
                                        >
                                            <span className='nav-item-text'>{localisedValues?.call_settings}</span>
                                        </button>
                                    </li>
                                    {isVirtualBackGroundFeatureAvailable() && !GLOBAL_MEETING_DATA.androidOSBelow15 && !isMobileSafariBelow18() && (
                                        <li className='nav-item' role='presentation'>
                                            <button
                                                className='nav-link'
                                                id='virtual-background-tab'
                                                data-bs-toggle='tab'
                                                data-bs-target='#virtual-background'
                                                type='button'
                                                role='tab'
                                                aria-selected='false'
                                            >
                                                <span className='nav-item-text'>{localisedValues?.virtual_background}</span>
                                                <span className='beta-label'>Beta</span>
                                            </button>
                                        </li>
                                    )}
                                    {isVirtualBackGroundFeatureAvailable() && (GLOBAL_MEETING_DATA.androidOSBelow15 || isMobileSafariBelow18()) && (
                                        <li className='nav-item' role='presentation'>
                                            <button
                                                className='nav-link'
                                                id='virtual-background-tab'
                                                type='button'
                                                role='tab'
                                                aria-selected='false'
                                                onClick={() => handleVBNotSupported()}
                                            >
                                                <span className='nav-item-text'>{localisedValues?.virtual_background}</span>
                                                <span className='beta-label'>Beta</span>
                                            </button>
                                        </li>
                                    )}
                                </ul>
                            </div>
                            <div className='tab-content call-settings-tab-content'>
                                <div className='tab-pane call-settings-tab-pane fade show active' id='call-settings' role='tabpanel'>
                                    <div className='call-settings call-settings-scroll custom-scroll'>
                                        <div className='call-settings-video'>
                                            <div className='call-settings-video-box'>
                                                {cameraPermission !== MEDIA_PERMISSIONS.BLOCKED && cameraPermission !== MEDIA_PERMISSIONS.ERROR && (
                                                    <div className='call-settings-video-loader' id='showCamLoading'>
                                                        <div className='round-loader default-loader'></div>
                                                    </div>
                                                )}

                                                {cameraPermission !== MEDIA_PERMISSIONS.BLOCKED && cameraPermission !== MEDIA_PERMISSIONS.ERROR && (
                                                    <div className='video' id='callsettingVideo'></div>
                                                )}
                                                {cameraPermission === MEDIA_PERMISSIONS.GRANTED && (
                                                    <div className='call-settings-video-2' id='localVideoContainer'></div>
                                                )}
                                                {(cameraPermission === MEDIA_PERMISSIONS.BLOCKED || cameraPermission === MEDIA_PERMISSIONS.ERROR) && (
                                                    <div className='call-settings-video-info'>
                                                        <div className='block-icon'>
                                                            <span className='for-icon icon-video-off'></span>
                                                        </div>
                                                        {cameraErrorMsg}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <CameraDropDown askPermission={askPermission} cameraDeviceUpdate={cameraDeviceUpdateHandler} />
                                        <MicrophoneDropDown askPermission={askPermission} audioDeviceUpdate={audioDeviceUpdateHandler} />
                                        {audioPermission === MEDIA_PERMISSIONS.GRANTED && (
                                            <div className='microphone-level' data-testid='microphone-level'>
                                                <div className='mic-level-box'></div>
                                                <div className='mic-level-box'></div>
                                                <div className='mic-level-box'></div>
                                                <div className='mic-level-box'></div>
                                                <div className='mic-level-box'></div>
                                            </div>
                                        )}
                                        {!PublicVariables.isMobile && <SpeakerDropDown askPermission={askPermission} />}
                                        {GLOBAL_MEETING_DATA.isAgent && <VideoQualityDropDown />}
                                        <div className='play-test-sound'>
                                            <button className='btn btn-primary' onClick={handlePlaySound}>
                                                <span className='for-icon icon-play'></span>
                                                <span className='for-text'>{localisedValues["play_test_sound"]}</span>
                                            </button>
                                        </div>
                                        <div id='callSettingError' className='validation-error another-video-error'></div>
                                        <div className='join-call-issue'>
                                            <h4>{localisedValues["having_an_issue_with_joining_the_call"]}</h4>
                                            <p>
                                                <button className='btn btn-link' onClick={openCallTestLink}>
                                                    {localisedValues["click_here"]}
                                                </button>{" "}
                                                {localisedValues["to_perform_network_check"]}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                {isVirtualBackGroundFeatureAvailable() && (
                                    <VirtualBackground cameraPermission={cameraPermission} cameraErrorMsg={cameraErrorMsg} />
                                )}
                            </div>
                        </div>
                        <div className='call-settings-loader' id='call_setting_loader_start'>
                            <div className='call-settings-loader-in'>
                                <div className='round-loader'></div>
                            </div>
                        </div>
                    </div>
                    {navigatorService.isMobileDevice() && (
                        <div className='modal-footer'>
                            <div className='button-inline'>
                                <div className='button'>
                                    <button className='btn btn-secondary' data-bs-dismiss='modal'>
                                        {localisedValues["close"]}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CallSettingDrawer;
